/**
 * POS Table Bill Lookup Component
 * Look up unpaid orders by table number for dine-in payment
 * Supports both full payment and split bill functionality
 * NEW: Allows adding more items to existing table orders
 */
import { useState } from 'react';
import { 
  Table, Search, CreditCard, Loader2, X, User, UserPlus,
  Clock, Check, Receipt, Users, Plus, ShoppingBag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';
import SplitBillModal from './SplitBillModal';

export default function POSTableBillLookup({ onPayBill, onClose, staffId, staffName, onAddItems }) {
  const [tableNumber, setTableNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [billData, setBillData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [processing, setProcessing] = useState(false);
  
  // Split bill state
  const [showSplitModal, setShowSplitModal] = useState(false);
  
  // Loyalty enrollment state
  const [enrollLoyalty, setEnrollLoyalty] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  const lookupTable = async () => {
    if (!tableNumber) {
      toast.error('Please enter a table number');
      return;
    }
    
    setLoading(true);
    setBillData(null);
    try {
      // Include staff_id for authentication
      const authParam = staffId ? `?staff_id=${staffId}` : '';
      const res = await api.get(`/pos/table-bill/${tableNumber}${authParam}`);
      const data = res.data;
      
      if (data.found) {
        setBillData(data);
        // Pre-fill customer info if available
        if (data.customer_info) {
          setCustomerEmail(data.customer_info.customer_email || '');
          setCustomerName(data.customer_info.customer_name || '');
          setCustomerPhone(data.customer_info.customer_phone || '');
        }
      } else {
        toast.info(`No unpaid orders found for Table ${tableNumber}`);
      }
    } catch (err) {
      toast.error('Failed to lookup table');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!billData) return;
    
    setProcessing(true);
    try {
      const res = await api.post('/pos/pay-table', {
        table_number: parseInt(tableNumber),
        payment_method: paymentMethod,
        order_ids: billData.order_ids,
        staff_id: staffId,
        enroll_loyalty: enrollLoyalty,
        customer_email: enrollLoyalty ? customerEmail : null,
        customer_name: enrollLoyalty ? customerName : null,
        customer_phone: enrollLoyalty ? customerPhone : null
      });
      
      const data = res.data;
      
      if (data.success) {
        toast.success(`Payment processed! ${data.orders_paid} order(s) paid - $${data.total_amount.toFixed(2)}`);
        if (data.loyalty_enrolled) {
          toast.success('Customer enrolled in loyalty program!');
        }
        if (onPayBill) {
          onPayBill(data);
        }
        onClose();
      } else {
        toast.error(data.detail || 'Payment failed');
      }
    } catch (err) {
      toast.error('Payment processing error');
    } finally {
      setProcessing(false);
    }
  };

  // Quick table buttons
  const quickTables = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="table-bill-lookup">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <Table className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Find Table Bill</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Look up unpaid dine-in orders</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {/* Table Number Input */}
          {!billData && (
            <>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Table className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="number"
                    placeholder="Enter table number..."
                    value={tableNumber}
                    onChange={(e) => setTableNumber(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && lookupTable()}
                    className="pl-12 h-14 text-lg bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white"
                    data-testid="table-number-input"
                  />
                </div>
                <Button
                  onClick={lookupTable}
                  disabled={loading || !tableNumber}
                  className="h-14 px-6 bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="lookup-table-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                </Button>
              </div>

              {/* Quick Table Selection */}
              <div className="grid grid-cols-6 gap-2">
                {quickTables.map((num) => (
                  <Button
                    key={num}
                    variant="outline"
                    onClick={() => {
                      setTableNumber(String(num));
                      setTimeout(lookupTable, 100);
                    }}
                    className="h-12 text-lg font-semibold bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                  >
                    {num}
                  </Button>
                ))}
              </div>
            </>
          )}

          {/* Bill Found */}
          {billData && (
            <div className="space-y-4">
              {/* Bill Header */}
              <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold text-indigo-900 dark:text-indigo-100">Table {tableNumber}</h3>
                  <span className="px-3 py-1 bg-indigo-600 text-white text-sm font-bold rounded-full">
                    {billData.order_count} Order{billData.order_count > 1 ? 's' : ''}
                  </span>
                </div>
                {billData.customer_info && (
                  <div className="flex items-center gap-2 text-sm text-indigo-700 dark:text-indigo-300">
                    <User className="w-4 h-4" />
                    <span>{billData.customer_info.customer_name || 'Guest'}</span>
                  </div>
                )}
              </div>

              {/* Order Items */}
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 max-h-48 overflow-y-auto">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Items</h4>
                <div className="space-y-2">
                  {billData.combined_items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-gray-700 dark:text-gray-300">
                        {item.quantity}x {item.name}
                      </span>
                      <span className="text-gray-900 dark:text-white font-medium">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="bg-white dark:bg-gray-700 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
                  <span className="text-gray-900 dark:text-white">${billData.combined_subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500 dark:text-gray-400">Tax (VAT)</span>
                  <span className="text-gray-900 dark:text-white">${billData.combined_tax?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold pt-2 border-t border-gray-200 dark:border-gray-600">
                  <span className="text-gray-900 dark:text-white">Total</span>
                  <span className="text-teal-600 dark:text-teal-400">${billData.combined_total?.toFixed(2)}</span>
                </div>
              </div>

              {/* Loyalty Enrollment */}
              {!billData.customer_info?.customer_id && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enrollLoyalty}
                      onChange={(e) => setEnrollLoyalty(e.target.checked)}
                      className="w-5 h-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                    />
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      <span className="font-medium text-amber-800 dark:text-amber-200">Enroll in Loyalty Program</span>
                    </div>
                  </label>
                  
                  {enrollLoyalty && (
                    <div className="mt-3 space-y-2">
                      <Input
                        type="email"
                        placeholder="Email address *"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        className="bg-white dark:bg-gray-700 border-amber-200 dark:border-amber-700 text-gray-900 dark:text-white"
                        required
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="text"
                          placeholder="Name"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          className="bg-white dark:bg-gray-700 border-amber-200 dark:border-amber-700 text-gray-900 dark:text-white"
                        />
                        <Input
                          type="tel"
                          placeholder="Phone"
                          value={customerPhone}
                          onChange={(e) => setCustomerPhone(e.target.value)}
                          className="bg-white dark:bg-gray-700 border-amber-200 dark:border-amber-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Method */}
              <div className="grid grid-cols-3 gap-2">
                {['cash', 'card', 'mpaisa'].map((method) => (
                  <Button
                    key={method}
                    variant={paymentMethod === method ? 'default' : 'outline'}
                    onClick={() => setPaymentMethod(method)}
                    className={`h-12 ${paymentMethod === method 
                      ? 'bg-teal-600 text-white hover:bg-teal-700' 
                      : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    {method === 'cash' && '💵 Cash'}
                    {method === 'card' && '💳 Card'}
                    {method === 'mpaisa' && '📱 M-PAiSA'}
                  </Button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* ADD MORE ITEMS - Prominent button for cashiers */}
                {onAddItems && (
                  <Button
                    onClick={() => {
                      // Load order into POS cart for editing
                      onAddItems({
                        tableNumber: parseInt(tableNumber),
                        orderIds: billData.order_ids,
                        items: billData.combined_items || [],
                        customer: billData.customer_info || null,
                        total: billData.combined_total,
                        subtotal: billData.combined_subtotal,
                        orderId: billData.order_ids?.[0] // Primary order to append to
                      });
                      toast.success(`Table ${tableNumber} loaded - Add items and process payment`, {
                        description: 'Browse menu to add more items'
                      });
                      onClose();
                    }}
                    className="w-full h-14 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-lg font-semibold shadow-lg"
                    data-testid="add-more-items-btn"
                  >
                    <div className="flex items-center gap-3">
                      <Plus className="w-6 h-6" />
                      <span>Add More Items</span>
                      <ShoppingBag className="w-5 h-5" />
                    </div>
                  </Button>
                )}

                {/* Payment Buttons Row */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setBillData(null)}
                    className="h-14 px-4 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                  >
                    Back
                  </Button>
                
                {/* Split Bill Button - PROMINENT */}
                <Button
                  onClick={() => setShowSplitModal(true)}
                  className="flex-1 h-14 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-lg font-semibold shadow-lg"
                  data-testid="split-bill-btn"
                >
                  <div className="flex flex-col items-center">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      <span>Split Bill</span>
                    </div>
                    <span className="text-[10px] opacity-80">Multiple People</span>
                  </div>
                </Button>
                
                <Button
                  onClick={handlePayment}
                  disabled={processing || (enrollLoyalty && !customerEmail)}
                  className="flex-1 h-14 bg-teal-600 hover:bg-teal-700 text-white text-lg font-semibold"
                  data-testid="pay-table-btn"
                >
                  {processing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        <span>Pay Full</span>
                      </div>
                      <span className="text-[10px] opacity-80">${billData.combined_total?.toFixed(2)}</span>
                    </div>
                  )}
                </Button>
              </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Split Bill Modal */}
      <SplitBillModal
        isOpen={showSplitModal}
        onClose={() => {
          setShowSplitModal(false);
          // Refresh the bill data in case items were paid
          if (tableNumber) {
            lookupTable();
          }
        }}
        tableNumber={parseInt(tableNumber)}
        staffId={staffId}
        staffName={staffName}
        onPaymentComplete={(result) => {
          if (result.table_fully_paid) {
            toast.success(`Table ${tableNumber} fully paid!`);
            onPayBill?.(result);
            onClose();
          }
        }}
      />
    </div>
  );
}
